import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  signal,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  ReactiveFormsModule,
  FormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

import { WarehouseWarrantDetailItem } from '@app/feature/center-business/stock/inbound/models/warehouse-warrant-detail-item';
import {
  DataTableComponent,
  TableColumn,
} from '@app/shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { formatNumberWithCommas } from '@app/shared/utils/number-formatter.util';

export interface StatusChangeDialogData {
  items: WarehouseWarrantDetailItem[];
  newStatus: string;
  totalQty?: number; // Add totalQty for planned quantity validation
}

export interface StatusChangeResult {
  confirmed: boolean;
  data?: {
    items: Array<{
      id?: number;
      skuCode?: string;
      skuName?: string;
      spuCode?: string;
      spuName?: string;
      plannedQty?: number;
      receivedQty: number;
      alreadyQty?: number;
    }>;
  };
}

@Component({
  selector: 'app-status-change-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    InputNumberModule,
    DialogModule,
    CheckboxModule,
    TooltipModule,
    ToastModule,
    DataTableComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './status-change-dialog.component.html',
  styleUrl: './status-change-dialog.component.css',
})
export class StatusChangeDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService);

  @Input() visible = false;
  @Input() data: StatusChangeDialogData | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() result = new EventEmitter<StatusChangeResult>();

  // Form
  statusForm!: FormGroup;
  itemsFormArray!: FormArray;

  // Table data for data-table component
  tableData = signal<any[]>([]);

  // Received all checkbox
  receivedAllChecked = false;

  // Table columns for data-table component
  tableColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_code',
      prop: 'skuCode',
      width: 100, // Reduced from 120
      type: 'text',
    },
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      width: 160, // Reduced from 200
      type: 'text',
    },
    {
      label: 'inbound.fields.spu_code',
      prop: 'spuCode',
      width: 100, // Reduced from 120
      type: 'text',
    },
    {
      label: 'inbound.fields.spu_name',
      prop: 'spuName',
      width: 160, // Reduced from 200
      type: 'text',
    },
    {
      label: 'inbound.fields.planned_quantity',
      prop: 'displayPlannedQty',
      width: 100, // Reduced from 120
      type: 'text',
      align: 'right',
      useGrouping: true,
    },
    {
      label: 'inbound.fields.already_received_quantity',
      prop: 'displayAlreadyQty',
      width: 100, // New column
      type: 'text',
      align: 'right',
      useGrouping: true,
    },
    {
      label: 'inbound.fields.received_quantity',
      prop: 'receivedQty',
      width: 150,
      type: 'inputNumberHorizontal',
      align: 'center',
      required: true,
      min: 0, // Allow 0 values to enable reset functionality
      max: 'maxReceivedQty', // Dynamic max value calculated as planned - already received
      useGrouping: true,
    },
    {
      label: 'inbound.fields.received_all',
      prop: 'receivedAll',
      width: 100,
      type: 'custom',
      align: 'center',
    },
  ];

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.statusForm = this.fb.group({
      items: this.fb.array([]),
    });
    this.itemsFormArray = this.statusForm.get('items') as FormArray;
  }

  ngOnChanges(): void {
    if (this.data && this.visible) {
      this.setupFormData();
    }
  }

  private setupFormData(): void {
    if (!this.data?.items) return;

    // Clear existing form array
    while (this.itemsFormArray.length !== 0) {
      this.itemsFormArray.removeAt(0);
    }

    // Prepare table data for data-table component
    const totalQty = this.data.totalQty || 0;
    const tableDataItems = this.data.items.map((item, index) => {
      const alreadyQty = item.alreadyQty || 0;
      const plannedQty = item.plannedQty || 0;
      const maxReceivedQty = Math.max(0, plannedQty - alreadyQty); // Calculate max as planned - already received
      const isFullyReceived = plannedQty === alreadyQty; // Check if item is fully received

      return {
        id: item.skuCode || `item_${index}`,
        skuCode: item.skuCode,
        skuName: item.skuName,
        spuCode: item.spuCode,
        spuName: item.spuName,
        plannedQty: plannedQty, // Use individual item's planned quantity
        displayPlannedQty: formatNumberWithCommas(plannedQty), // For display
        alreadyQty: alreadyQty, // Store already received quantity
        displayAlreadyQty: formatNumberWithCommas(alreadyQty), // For display
        maxReceivedQty: maxReceivedQty, // Maximum allowed received quantity
        receivedQty: isFullyReceived ? 0 : item.receivedQty || 0, // Set to 0 if fully received
        receivedAll: false, // Initialize received all checkbox as unchecked
        isFullyReceived: isFullyReceived, // Track if item is fully received
      };
    });

    this.tableData.set(tableDataItems);

    // Add form controls for each item
    this.data.items.forEach((item, index) => {
      const alreadyQty = item.alreadyQty || 0;
      const plannedQty = item.plannedQty || 0;
      const maxReceivedQty = Math.max(0, plannedQty - alreadyQty); // Calculate max as planned - already received
      const isFullyReceived = plannedQty === alreadyQty; // Check if item is fully received

      const itemGroup = this.fb.group({
        skuCode: [item.skuCode],
        skuName: [item.skuName],
        spuCode: [item.spuCode],
        spuName: [item.spuName],
        plannedQty: [plannedQty], // Use individual item's planned quantity
        alreadyQty: [alreadyQty], // Store already received quantity
        maxReceivedQty: [maxReceivedQty], // Store maximum allowed received quantity
        receivedQty: [
          isFullyReceived ? 0 : item.receivedQty || 0, // Set to 0 if fully received
          [
            Validators.min(0), // Allow 0 values - users can reset to 0
            Validators.max(maxReceivedQty), // Use calculated max for validation
          ],
        ],
        receivedAll: [false], // Add received all checkbox control
        isFullyReceived: [isFullyReceived], // Track if item is fully received
      });

      this.itemsFormArray.push(itemGroup);
    });
  }

  /**
   * Sync table data to form controls to ensure consistency
   */
  private syncTableDataToFormControls(): void {
    const currentTableData = this.tableData();

    currentTableData.forEach((item, index) => {
      if (this.itemsFormArray.at(index)) {
        const control = this.itemsFormArray.at(index).get('receivedQty');
        const tableValue = item.receivedQty || 0;
        const formValue = control?.value || 0;

        if (tableValue !== formValue) {
          control?.setValue(tableValue);
        }
      }
    });
  }

  onConfirm(): void {
    // Sync table data to form controls before validation
    this.syncTableDataToFormControls();

    // Only validate business rules, not form validation
    const validationErrors = this.validateBusinessRules();
    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant(
            'inbound.messages.status_dialog_validation_error'
          ),
          detail: error,
        });
      });
      return;
    }

    // Prepare result data from form values with complete item data
    const resultData = {
      items: this.itemsFormArray.controls.map((control, index) => {
        const receivedQtyControl = control.get('receivedQty');
        const receivedQty = Number(receivedQtyControl?.value) || 0;
        const originalItem = this.data?.items[index];

        // Return complete item data with updated receivedQty
        return {
          id: originalItem?.id,
          skuCode: originalItem?.skuCode,
          skuName: originalItem?.skuName,
          spuCode: originalItem?.spuCode,
          spuName: originalItem?.spuName,
          plannedQty: originalItem?.plannedQty,
          receivedQty: receivedQty, // Updated value from form
          alreadyQty: originalItem?.alreadyQty,
        };
      }),
    };

    this.result.emit({
      confirmed: true,
      data: resultData,
    });

    this.closeDialog();
  }

  onCancel(): void {
    this.result.emit({ confirmed: false });
    this.closeDialog();
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private validateBusinessRules(): string[] {
    const errors: string[] = [];
    const items = this.tableData();

    // Check if at least one item has received quantity > 0
    const hasAtLeastOneReceivedQty = items.some((item: any) => {
      const receivedQty = Number(item.receivedQty) || 0;
      return receivedQty > 0;
    });

    if (!hasAtLeastOneReceivedQty) {
      errors.push(
        this.translateService.instant(
          'inbound.messages.at_least_one_received_quantity_required'
        )
      );
    }

    items.forEach((item: any, index: number) => {
      const receivedQty = Number(item.receivedQty) || 0;
      const maxReceivedQty = item.maxReceivedQty || 0;

      // Rule: Received Quantity must be ≤ Maximum Allowed (planned - already received)
      // Allow 0 values, but validate against maximum
      if (receivedQty > maxReceivedQty) {
        errors.push(
          this.translateService.instant(
            'inbound.messages.received_quantity_exceeds_planned',
            {
              row: index + 1,
              received: formatNumberWithCommas(receivedQty),
              planned: formatNumberWithCommas(maxReceivedQty),
            }
          )
        );
      }
    });

    return errors;
  }

  getFieldError(controlName: string, index: number): string {
    const itemControl = this.itemsFormArray.at(index);
    const control = itemControl.get(controlName);

    if (control?.errors && control.touched) {
      if (control.errors['required']) {
        return this.translateService.instant(
          'inbound.messages.status_dialog_field_required'
        );
      }
      if (control.errors['min']) {
        return this.translateService.instant(
          'inbound.messages.status_dialog_minimum_value',
          {
            min: control.errors['min'].min,
          }
        );
      }
    }
    return '';
  }

  formatNumber(value: any): string {
    if (!value || value === '' || value === 'undefined' || value === 'null') {
      return '0';
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      return '0';
    }

    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  onTableDataChange(event: any): void {
    // Handle data changes from the data-table component
    if (event?.data) {
      // Update data while preserving plannedQty as number and displayPlannedQty as formatted
      const updatedData = event.data.map((item: any, index: number) => {
        const updatedItem = {
          ...item,
          plannedQty: item.plannedQty || 0, // Keep as number for max constraint
          displayPlannedQty: formatNumberWithCommas(item.plannedQty || 0), // For display
          receivedQty: item.receivedQty || 0,
        };

        // Sync the change back to the form control
        if (this.itemsFormArray.at(index)) {
          const control = this.itemsFormArray.at(index).get('receivedQty');
          control?.setValue(item.receivedQty || 0);
        }

        return updatedItem;
      });
      this.tableData.set(updatedData);
    }
  }

  // Method to handle number input formatting
  onNumberInputChange(value: any, field: string, index: number): void {
    const currentData = this.tableData();
    const updatedData = [...currentData];

    if (updatedData[index]) {
      updatedData[index][field] = value || 0;
      this.tableData.set(updatedData);

      // Sync the change back to the form control
      if (this.itemsFormArray.at(index)) {
        const control = this.itemsFormArray.at(index).get(field);
        control?.setValue(value || 0);
      }

      // Update "Received all" checkbox state based on individual input changes
      this.updateReceivedAllCheckboxState();
    }
  }

  // Method to handle "Received all" checkbox change
  onReceivedAllChange(checked: boolean): void {
    this.receivedAllChecked = checked;

    if (checked) {
      // Set all received quantities to their maximum possible values (planned - already received)
      const currentData = this.tableData();
      const updatedData = currentData.map((item: any) => ({
        ...item,
        receivedQty: item.maxReceivedQty || 0,
        receivedAll: true, // Also check the individual received all checkboxes
      }));
      this.tableData.set(updatedData);

      // Sync the changes back to the form controls
      this.itemsFormArray.controls.forEach((control, index) => {
        const maxReceivedQty = control.get('maxReceivedQty')?.value || 0;
        control.get('receivedQty')?.setValue(maxReceivedQty);
        control.get('receivedAll')?.setValue(true); // Also update the individual checkbox
      });
    } else {
      // When unchecked, only uncheck all individual checkboxes but keep the current received quantities
      const currentData = this.tableData();
      const updatedData = currentData.map((item: any) => ({
        ...item,
        // Keep the current receivedQty value - don't reset to 0
        receivedAll: false, // Only uncheck the individual received all checkboxes
      }));
      this.tableData.set(updatedData);

      // Sync the changes back to the form controls
      this.itemsFormArray.controls.forEach((control, index) => {
        // Keep the current receivedQty value - don't reset to 0
        control.get('receivedAll')?.setValue(false); // Only update the individual checkbox
      });
    }
  }

  // Method to handle individual row "Received all" checkbox change
  onRowReceivedAllChange(checked: boolean, rowData: any): void {
    const currentData = this.tableData();
    const updatedData = [...currentData];

    // Find the index of the row in the data array
    const index = currentData.findIndex((item) => item.id === rowData.id);

    if (index !== -1 && updatedData[index]) {
      if (checked) {
        // Set received quantity to maximum possible value (planned - already received)
        updatedData[index].receivedQty = updatedData[index].maxReceivedQty || 0;
        updatedData[index].receivedAll = true;
      } else {
        // When unchecked, only uncheck the checkbox but keep the current received quantity
        updatedData[index].receivedAll = false;
        // Keep the current receivedQty value - don't reset to 0
      }

      this.tableData.set(updatedData);

      // Sync the changes back to the form controls
      if (this.itemsFormArray.at(index)) {
        const control = this.itemsFormArray.at(index);
        if (checked) {
          control.get('receivedQty')?.setValue(updatedData[index].receivedQty);
        }
        control.get('receivedAll')?.setValue(updatedData[index].receivedAll);
      }

      // Update the global "Received all" checkbox state
      this.updateReceivedAllCheckboxState();
    }
  }

  /**
   * Update "Received all" checkbox state based on individual quantity inputs and checkbox states
   * - If all items have receivedQty >= maxReceivedQty AND all individual receivedAll checkboxes are checked, check the global checkbox
   * - If any item has receivedQty < maxReceivedQty OR any individual receivedAll checkbox is unchecked, uncheck the global checkbox
   */
  private updateReceivedAllCheckboxState(): void {
    const currentData = this.tableData();

    // Check if all items have received quantity >= maximum possible received quantity AND all individual checkboxes are checked
    const allItemsFullyReceived = currentData.every((item: any) => {
      const receivedQty = item.receivedQty || 0;
      const maxReceivedQty = item.maxReceivedQty || 0;
      const isFullyReceived = receivedQty >= maxReceivedQty;
      const isIndividualChecked = item.receivedAll === true;

      return isFullyReceived && isIndividualChecked;
    });

    // Update checkbox state if it's different from current state
    if (this.receivedAllChecked !== allItemsFullyReceived) {
      this.receivedAllChecked = allItemsFullyReceived;

      // Trigger change detection to update the UI
      this.cdr.detectChanges();
    }
  }
}
